/**
 * Shared team roster for collaborative surfaces.
 *
 * The active tenant's team members feed BOTH the assignee picker and the
 * collaboration mention/comment-author roster, so "who can I assign / @-mention"
 * is one consistent list across the per-transaction workspace and the
 * standalone investigation workspace.
 *
 * Every read here reports its own failure. A roster that empties itself on a
 * failed fetch is not a smaller roster, it is a false one: the assignee picker
 * offers "Unassigned" alone, the mention list silently omits the colleague you
 * are trying to reach, and the collaboration transport concludes you belong to
 * no team and falls back to a channel that reaches nobody.
 */
import * as React from "react";
import { useSession } from "@authe/ui-core/session";
import { useOptionalStreamingClient } from "@authe/streaming";
import type { Member } from "@authe/fe-commons/teams";
import type { CollabUser } from "@authe/collaboration";
import { useTeamsApi } from "@/api/adapters";
import { useActiveTeam } from "./activeTeam";

/** A roster read: the rows, plus whether they are an answer. */
export interface RosterRead<T> {
  rows: T;
  isLoading: boolean;
  /** The most recent read failed; `rows` may hold an earlier answer. */
  failed: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * The ACTIVE team's members (assignee options + mention roster), with the
 * read's own outcome.
 *
 * Scoped to `useActiveTeam`, not to the tenant. It used to build its own ref
 * from the tenant id — the backend's primary-team alias — so in a tenant with
 * more than one team the picker always listed the DEFAULT team's members and
 * there was no way to assign or @-mention anyone on the team you were actually
 * working in.
 */
export function useTeamMembersRead(): RosterRead<Member[]> {
  const api = useTeamsApi();
  const { team } = useActiveTeam();

  const [state, setState] = React.useState<{
    members: Member[];
    isLoading: boolean;
    error: unknown;
  }>({ members: [], isLoading: true, error: null });
  const [attempt, setAttempt] = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    setState((prev) => ({ ...prev, isLoading: prev.members.length === 0, error: null }));
    api
      .listMembers(team)
      .then((members) => {
        if (alive) setState({ members, isLoading: false, error: null });
      })
      .catch((error: unknown) => {
        // Keep whatever roster is already on screen; a failed refresh must not
        // retract the colleagues a picker is currently offering.
        if (alive)
          setState((prev) => ({
            members: prev.members,
            isLoading: false,
            error: error ?? new Error("Could not load the team's members"),
          }));
      });
    return () => {
      alive = false;
    };
  }, [api, team, attempt]);

  return {
    rows: state.members,
    isLoading: state.isLoading,
    failed: state.error != null,
    error: state.error,
    refetch: React.useCallback(() => setAttempt((n) => n + 1), []),
  };
}

/** The active team's members. Prefer {@link useTeamMembersRead} when writing. */
export function useTeamMembers(): Member[] {
  return useTeamMembersRead().rows;
}

/**
 * The teams the caller belongs to, as ids, with the read's own outcome.
 *
 * Both live surfaces need these: collaboration fans a room out over one channel
 * per team, and the transaction stream publishes team-targeted rows onto
 * `{org}-{tenant}-team#<id>-streamed-transactions`, so a subscriber that omits
 * its teams silently misses those rows. Only fetched when a streaming client is
 * mounted, since team channels are the only thing the ids are for — which is
 * why `streaming` is reported rather than folded into an empty list: "no live
 * client" and "no teams" send a collaborative surface down the same
 * broadcast-only path for completely different reasons, and the person typing
 * into it deserves the right sentence.
 */
export interface TeamIdsRead extends RosterRead<string[]> {
  /** A live streaming client is mounted (team channels are otherwise pointless). */
  streaming: boolean;
  /** This build wires no memberships endpoint. */
  unavailable: boolean;
}

export function useTeamIdsRead(): TeamIdsRead {
  const { session } = useSession();
  const streaming = useOptionalStreamingClient();
  const teamsApi = useTeamsApi();
  const userId = session?.user.id ?? null;
  const identity = JSON.stringify([session?.tenant.customerId, session?.tenant.orgId, session?.tenant.tenantId, userId]);
  const listMemberships = teamsApi.listMemberships;

  const [state, setState] = React.useState<{
    identity?: string;
    teamIds: string[];
    isLoading: boolean;
    error: unknown;
  }>({ teamIds: [], isLoading: true, error: null });
  const [attempt, setAttempt] = React.useState(0);

  React.useEffect(() => {
    if (!userId || !streaming || !listMemberships) {
      setState({ identity, teamIds: [], isLoading: false, error: null });
      return undefined;
    }
    let alive = true;
    setState((prev) => ({ identity, teamIds: prev.identity === identity ? prev.teamIds : [], isLoading: true, error: null }));
    listMemberships(userId)
      .then((ms) => {
        if (!alive) return;
        setState({
          identity,
          teamIds: ms.map((m) => m.team.id).filter((t): t is string => Boolean(t)),
          isLoading: false,
          error: null,
        });
      })
      .catch((error: unknown) => {
        if (!alive) return;
        setState((prev) => ({
          identity,
          teamIds: prev.identity === identity ? prev.teamIds : [],
          isLoading: false,
          error: error ?? new Error("Could not load your teams"),
        }));
      });
    return () => {
      alive = false;
    };
  }, [identity, userId, streaming, listMemberships, attempt]);

  return {
    rows: state.identity === identity ? state.teamIds : [],
    isLoading: (state.identity !== identity || state.isLoading) && Boolean(userId) && Boolean(streaming) && Boolean(listMemberships),
    failed: state.error != null,
    error: state.error,
    streaming: Boolean(streaming),
    unavailable: !listMemberships,
    refetch: React.useCallback(() => setAttempt((n) => n + 1), []),
  };
}

/** The caller's team ids. Prefer {@link useTeamIdsRead} on a live surface. */
export function useTeamIds(): string[] {
  return useTeamIdsRead().rows;
}

export function toCollabUser(m: Member): CollabUser {
  return { id: m.id, name: m.name || m.email || m.id, avatarUrl: m.avatarUrl };
}

/**
 * The local user (`me`) + the mention/author roster (`me` first, then the
 * tenant's other members) for a collaboration surface. `me` is `null` until the
 * session resolves a user id.
 */
export function useCollabRoster(): {
  me: CollabUser | null;
  users: CollabUser[];
  /** The roster read failed, so `users` may be missing mentionable people. */
  failed: boolean;
} {
  const { session } = useSession();
  const { rows: members, failed } = useTeamMembersRead();
  return React.useMemo(() => {
    if (!session?.user.id) return { me: null, users: [], failed };
    const me: CollabUser = {
      id: session.user.id,
      name: session.user.name || session.user.email || session.user.id,
      avatarUrl: session.user.avatar ?? undefined,
    };
    const users: CollabUser[] = [me, ...members.filter((m) => m.id !== me.id).map(toCollabUser)];
    return { me, users, failed };
  }, [session, members, failed]);
}
